/**
 * @name PreviousRouteService
 * @description Servicio para redirigir a una ruta anterior
 * @author Alexis Becerra
 */

const STORAGE_KEY = 'ls.URLToRedirect';
const LOGIN_ROUTE_PATTERN = /^\/login(?:[/?#]|$)/i;

const safelyParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getHashRoute = () => {
  const [, hashRoute = ''] = window.location.href.split('#');
  return hashRoute || '';
};

const getCurrentAppBasePath = () => {
  const { pathname = '/' } = window.location;

  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname : `${pathname}/`;
};

const normalizeRoute = (route = '') => {
  if (!route) {
    return '';
  }

  const trimmedRoute = String(route).trim();

  if (!trimmedRoute) {
    return '';
  }

  if (trimmedRoute.startsWith('#')) {
    return normalizeRoute(trimmedRoute.substring(1));
  }

  if (trimmedRoute.startsWith('/')) {
    return trimmedRoute;
  }

  if (trimmedRoute.startsWith('?')) {
    return `/${trimmedRoute}`;
  }

  return `/${trimmedRoute}`;
};

const normalizeAppBasePath = (appBasePath = '') => {
  if (!appBasePath) {
    return '';
  }

  const trimmedPath = String(appBasePath).trim();

  if (!trimmedPath || trimmedPath === '/') {
    return '/';
  }

  const leadingSlashPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
  return leadingSlashPath.endsWith('/') ? leadingSlashPath : `${leadingSlashPath}/`;
};

const buildFullRedirectUrl = (route, appBasePath) => {
  const normalizedRoute = normalizeRoute(route);
  const normalizedAppBasePath = normalizeAppBasePath(appBasePath);

  if (!normalizedRoute || !normalizedAppBasePath) {
    return '';
  }

  return `${window.location.origin}${normalizedAppBasePath}#${normalizedRoute}`;
};

const shouldIgnoreRoute = (route) => {
  const normalizedRoute = normalizeRoute(route);
  return !normalizedRoute || LOGIN_ROUTE_PATTERN.test(normalizedRoute);
};

const getRouteFromLocation = (location = {}) => {
  const {
    pathname = '',
    search = '',
    hash = ''
  } = location;

  return pathname || search || hash ? `${pathname}${search}${hash}` : '';
};

const normalizeStoredRoute = (storedRoute) => {
  if (!storedRoute || typeof storedRoute !== 'object') {
    return null;
  }

  const routeToRedirect = normalizeRoute(storedRoute.routeToRedirect);
  const appBasePath = normalizeAppBasePath(storedRoute.appBasePath);

  return {
    routeToRedirect,
    rootRoute: storedRoute.rootRoute || '/',
    appBasePath,
    fullRedirectUrl: storedRoute.fullRedirectUrl || (appBasePath ? buildFullRedirectUrl(routeToRedirect, appBasePath) : ''),
    source: storedRoute.source || ''
  };
};

const setPreviousRoute = (route, rootRoute = '/', options = {}) => {
  const normalizedRoute = normalizeRoute(route);

  if (shouldIgnoreRoute(normalizedRoute)) {
    return;
  }

  const appBasePath = normalizeAppBasePath(options.appBasePath || getCurrentAppBasePath());
  const fullRedirectUrl = options.fullRedirectUrl || buildFullRedirectUrl(normalizedRoute, appBasePath);

  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    routeToRedirect: normalizedRoute,
    rootRoute,
    appBasePath,
    fullRedirectUrl,
    source: options.source || ''
  }));
};

const getPreviousRoute = () => {
  return normalizeStoredRoute(safelyParse(localStorage.getItem(STORAGE_KEY)));
};

const cleanPreviousRoute = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const getRedirectPath = (locationState = null, fallbackPath = '/home') => {
  const savedRoute = getPreviousRoute()?.routeToRedirect;
  const routeFromState = normalizeRoute(getRouteFromLocation(locationState?.from));
  return savedRoute || routeFromState || fallbackPath;
};

const redirectFromLogin = (history, locationState = null, fallbackPath = '/home') => {
  const savedRoute = getPreviousRoute();
  const currentAppBasePath = normalizeAppBasePath(getCurrentAppBasePath());
  const redirectPath = getRedirectPath(locationState, fallbackPath);

  if (
    savedRoute?.appBasePath &&
    savedRoute.appBasePath !== currentAppBasePath &&
    savedRoute.fullRedirectUrl
  ) {
    cleanPreviousRoute();
    window.location.assign(savedRoute.fullRedirectUrl);
    return;
  }

  cleanPreviousRoute();
  history.push(redirectPath);
};

const saveRouteFromExpiredSession = (route, rootRoute = '/') => {
  setPreviousRoute(route, rootRoute, { source: 'expired-session' });
};

const saveCurrentRoute = (rootRoute = '/') => {
  setPreviousRoute(getHashRoute(), rootRoute, { source: 'current-route' });
};

export const PreviousRouteService = {
  setPreviousRoute,
  getPreviousRoute,
  getRouteFromLocation,
  getRedirectPath,
  cleanPreviousRoute,
  redirectFromLogin,
  saveRouteFromExpiredSession,
  saveCurrentRoute
};
